import ArrowCircleDown from '@mui/icons-material/ArrowCircleDown';
import Check from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { InvoiceCategoryFilter } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useRef, useState } from 'react';

import { Chip } from '~components/Chip/Chip';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import TreadMenu, { MenuHandler } from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { Skeleton } from '~components/Skeleton/Skeleton';
import { Invoice } from '~hooks/useInvoices/models';

import { isInvoiceApproved } from './invoiceApproval';

// The field/job approve, when the current user is a field approver. Rendered
// beside the billing (Rec/Pay) approvals in the same cluster.
interface JobApprovalConfig {
  label: string;
  approved: boolean;
  approvable: boolean;
  unapprovable: boolean;
  loading: boolean;
  onApprove: () => void;
  onUnapprove: () => void;
}

// The Summary-header actions: one permission-scoped approve cluster (billing
// Rec/Pay for billing approvers, a single field approve for field approvers).
// Editing is handled by contextual pencils on each section, not a menu here.
interface JobInvoiceActionsProps {
  payable: Invoice | null;
  receivable: Invoice | null;
  onApproveSide: (side: InvoiceCategoryFilter) => void;
  onUnapproveSide: (side: InvoiceCategoryFilter) => Promise<void>;
  // Billing approvals (Rec/Pay) render only when the user can billing-approve.
  showBillingApprovals: boolean;
  // Field/job approve — null when the user isn't a field approver.
  jobApproval: JobApprovalConfig | null;
  loadingInvoices?: boolean;
}

export default function JobInvoiceActions({
  payable,
  receivable,
  onApproveSide,
  onUnapproveSide,
  showBillingApprovals,
  jobApproval,
  loadingInvoices,
}: JobInvoiceActionsProps) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  const [sideToUnapprove, setSideToUnapprove] = useState<InvoiceCategoryFilter | null>(
    null,
  );
  const [isUnapproving, setIsUnapproving] = useState(false);

  const hasBilling = showBillingApprovals && Boolean(receivable || payable);
  const hasField = Boolean(
    jobApproval && (jobApproval.approved || jobApproval.approvable),
  );
  const showBillingSkeleton =
    showBillingApprovals && !hasBilling && Boolean(loadingInvoices);

  const handleConfirmUnapprove = async () => {
    if (sideToUnapprove === null) {
      return;
    }
    try {
      setIsUnapproving(true);
      await onUnapproveSide(sideToUnapprove);
    } finally {
      setIsUnapproving(false);
      setSideToUnapprove(null);
    }
  };

  return (
    <>
      {(hasBilling || hasField || showBillingSkeleton) && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '.05em',
              color: c.treadGray3,
            }}
          >
            {t('actions.approve')}
          </Typography>
          {showBillingSkeleton && (
            <>
              <Skeleton variant="rounded" width={44} height={28} />
              <Skeleton variant="rounded" width={44} height={28} />
            </>
          )}
          {hasBilling && (
            <>
              <SideApproveButton
                approved={isInvoiceApproved(receivable)}
                domain="billing"
                disabled={
                  !receivable ||
                  (!isInvoiceApproved(receivable) && !receivable.approvable)
                }
                label={t('approvals.approve_jobs.invoice_matrix.receivable')}
                onClick={() => onApproveSide(InvoiceCategoryFilter.RECEIVABLES)}
                onUnapprove={() => setSideToUnapprove(InvoiceCategoryFilter.RECEIVABLES)}
                tooltip={t('approvals.approve_jobs.invoice_matrix.approve_receivable')}
                unapprovable={Boolean(receivable?.unapprovable)}
              />
              <SideApproveButton
                approved={isInvoiceApproved(payable)}
                domain="billing"
                disabled={
                  !payable || (!isInvoiceApproved(payable) && !payable.approvable)
                }
                label={t('approvals.approve_jobs.invoice_matrix.payable')}
                onClick={() => onApproveSide(InvoiceCategoryFilter.PAYABLES)}
                onUnapprove={() => setSideToUnapprove(InvoiceCategoryFilter.PAYABLES)}
                tooltip={t('approvals.approve_jobs.invoice_matrix.approve_payable')}
                unapprovable={Boolean(payable?.unapprovable)}
              />
            </>
          )}
          {hasField && jobApproval && (
            <SideApproveButton
              approved={jobApproval.approved}
              domain="field"
              disabled={jobApproval.loading}
              label={jobApproval.label}
              onClick={jobApproval.onApprove}
              onUnapprove={jobApproval.onUnapprove}
              tooltip={t('approvals.approve_jobs.summary_hero.approve_job_hint')}
              unapprovable={jobApproval.unapprovable}
            />
          )}
        </Box>
      )}

      <ConfirmationDialog
        confirmButtonProps={{
          children: t('approvals.approve_jobs.unapprove_invoice_dialog.confirm_button'),
        }}
        isBusy={isUnapproving}
        isOpen={sideToUnapprove !== null}
        onClose={() => setSideToUnapprove(null)}
        onConfirm={handleConfirmUnapprove}
        title={t('approvals.approve_jobs.unapprove_invoice_dialog.title')}
      >
        <Typography>
          {t('approvals.approve_jobs.unapprove_invoice_dialog.confirm_prompt')}
        </Typography>
        <Typography sx={{ mt: 2 }}>
          {t('approvals.approve_jobs.unapprove_invoice_dialog.sync_warning')}
        </Typography>
      </ConfirmationDialog>
    </>
  );
}

function SideApproveButton({
  approved,
  disabled,
  domain,
  label,
  tooltip,
  onClick,
  onUnapprove,
  unapprovable,
}: {
  approved: boolean;
  disabled: boolean;
  // Which approval this is — the two domains the rail's Approvals card names. Billing
  // (Receivable/Payable) and Field (Job) take different fills so the cluster shows at a
  // glance which kind of sign-off each button is.
  domain: 'billing' | 'field';
  label: string;
  tooltip: string;
  onClick: () => void;
  onUnapprove: () => void;
  unapprovable: boolean;
}) {
  const theme = useTheme();
  const menuHandler = useRef<MenuHandler>(null);

  if (approved) {
    const chip = (
      <Chip
        color="success"
        hideDropdownArrow={!unapprovable}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Check sx={{ fontSize: '13px' }} />
            {label}
          </Box>
        }
      />
    );

    if (!unapprovable) {
      return <Box sx={{ display: 'inline-block' }}>{chip}</Box>;
    }

    return (
      <TreadMenu
        ref={menuHandler}
        menuTrigger={
          <Box sx={{ cursor: 'pointer', display: 'inline-block' }}>{chip}</Box>
        }
      >
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            menuHandler.current?.onClose?.();
            onUnapprove();
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <ArrowCircleDown
              sx={{
                color: theme.brandV2.colors.treadGray3,
                fontSize: '15px',
                transform: 'rotate(90deg)',
              }}
            />
            {t('approvals.driver_day.unapprove')}
          </Box>
        </MenuItem>
      </TreadMenu>
    );
  }

  return (
    // Approving is what this page exists for, so the unapproved control takes the
    // system's commit treatment (contained green — the same one Save and the legacy
    // Approve use) rather than the Cancel-grade gray outline it wore before.
    <Tooltip title={tooltip} placement="bottom">
      <span>
        <SmallButton
          color={domain === 'field' ? 'brandV2Green' : 'brandV2Yellow'}
          disabled={disabled}
          disableElevation
          onClick={onClick}
          size="small"
          variant="contained"
          sx={{ fontWeight: 700 }}
        >
          {label}
        </SmallButton>
      </span>
    </Tooltip>
  );
}
